'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddLocationAltRoundedIcon from '@mui/icons-material/AddLocationAltRounded';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';

import { GQLMutation } from '@/lib/client';
import { M_AGENT_LOCATION_OPTIONS } from '@/lib/mutations/location.mutation';
import type {
  AgentLocationOption,
  AgentLocationPreference,
} from '@/lib/interface/location.interface';

export const agentLocationKey = (location: AgentLocationPreference): string => {
  if (location.key) return location.key;
  if (location.source === 'LEGACY') return `legacy:${location.locationId}`;
  if (location.source === 'GEOCODER') {
    return `geocoder:${Number(location.latitude).toFixed(6)}:${Number(location.longitude).toFixed(6)}`;
  }
  return `custom:${location.label.trim().toLocaleLowerCase().replace(/\s+/g, ' ')}`;
};

type Props = {
  value: AgentLocationPreference[];
  onChange: (value: AgentLocationPreference[]) => void;
  label?: string;
  placeholder?: string;
  helperText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  allowManual?: boolean;
};

export default function AgentLocationPreferencePicker({
  value,
  onChange,
  label,
  placeholder = 'Search a town, area, ward, or neighbourhood…',
  helperText,
  error,
  disabled,
  allowManual = true,
}: Props) {
  const [input, setInput] = useState('');
  const [options, setOptions] = useState<AgentLocationOption[]>([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualLabel, setManualLabel] = useState('');
  const sequence = useRef(0);

  const { action: searchLocations, loading } = GQLMutation({
    mutation: M_AGENT_LOCATION_OPTIONS,
    resolver: 'agentLocationOptions',
    toastmsg: false,
    silentError: true,
  });

  const selectedOptions = useMemo<AgentLocationOption[]>(
    () => (value || []).map((location) => ({
      ...location,
      key: agentLocationKey(location),
    })),
    [value],
  );

  const allOptions = useMemo(() => {
    const merged = new Map<string, AgentLocationOption>();
    selectedOptions.forEach((option) => merged.set(option.key, option));
    options.forEach((option) => merged.set(option.key, option));
    return Array.from(merged.values());
  }, [options, selectedOptions]);

  useEffect(() => {
    const query = input.trim();
    const current = ++sequence.current;
    if (query.length < 3) {
      setOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const result: any = await searchLocations({
          variables: { input: { query, limit: 8 } },
        });
        if (current !== sequence.current) return;
        setOptions(result?.data?.agentLocationOptions || []);
      } catch {
        if (current === sequence.current) setOptions([]);
      }
    }, 400);
    return () => clearTimeout(timer);
    // GQLMutation returns a render-local action; input is the intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const addManualLocation = () => {
    const normalized = manualLabel.trim().replace(/\s+/g, ' ');
    if (normalized.length < 2) return;
    const next: AgentLocationPreference = {
      source: 'CUSTOM',
      label: normalized,
    };
    const key = agentLocationKey(next);
    if (!(value || []).some((item) => agentLocationKey(item) === key)) {
      onChange([...(value || []), next]);
    }
    setManualLabel('');
    setManualOpen(false);
  };

  return (
    <Stack spacing={1}>
      <Autocomplete<AgentLocationOption, true, false, false>
        multiple
        disabled={disabled}
        options={allOptions}
        value={selectedOptions}
        loading={loading}
        filterOptions={(items) => items}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, selected) => option.key === selected.key}
        onInputChange={(_, next, reason) => {
          if (reason === 'input') setInput(next);
        }}
        onChange={(_, next) => onChange(next)}
        renderOption={(props, option) => (
          <li {...props} key={option.key}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
              <LocationOnRoundedIcon fontSize="small" color="action" />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{option.label}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {option.source === 'LEGACY'
                    ? 'Existing location'
                    : option.source === 'CUSTOM'
                      ? 'Manually entered'
                      : option.placeType || 'Mapped location'}
                </Typography>
              </Box>
            </Stack>
          </li>
        )}
        renderTags={(items, getTagProps) => items.map((option, index) => (
          <Chip
            {...getTagProps({ index })}
            key={option.key}
            size="small"
            variant="outlined"
            label={option.label}
            color={option.source === 'LEGACY' ? 'default' : 'primary'}
          />
        ))}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            inputProps={{ ...params.inputProps, autoComplete: 'new-password' }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        noOptionsText={input.trim().length < 3 ? 'Type at least 3 characters' : 'No mapped locations found'}
      />

      {allowManual && (
        <Box>
          <Button
            type="button"
            size="small"
            variant="text"
            startIcon={<AddLocationAltRoundedIcon />}
            onClick={() => setManualOpen(true)}
          >
            Location not listed? Add it manually
          </Button>
        </Box>
      )}

      <Dialog open={manualOpen} onClose={() => setManualOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a location manually</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label="Town, area, region, and country"
            value={manualLabel}
            onChange={(event) => setManualLabel(event.target.value)}
            inputProps={{ maxLength: 512 }}
            helperText="Use this when the mapped search does not cover your location."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addManualLocation} disabled={manualLabel.trim().length < 2}>
            Add location
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
